import React, { useState, useEffect } from "react";
import { apiFetch } from "../services/api";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

const UserManagement = ({ user, hideHeader = false }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [successMessage, setSuccessMessage] = useState("");
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    phone: "",
    role: "Viewer",
  });

  const [editUser, setEditUser] = useState({
    name: "",
    phone: "",
    role: "Viewer",
    is_active: true,
  });

  const roles = [
    { value: "Administrator", label: "Administrator" },
    { value: "Editor", label: "Editor" },
    { value: "Viewer", label: "Viewer" },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiFetch("/api/auth/users");
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        setError("Failed to fetch users");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    // Debug: Log the data being sent
    console.log("🔍 Sending user data:", newUser);
    console.log("🔍 User data JSON:", JSON.stringify(newUser, null, 2));

    try {
      const response = await apiFetch("/api/auth/users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newUser),
      });

      console.log("📡 Response status:", response.status);

      if (response.ok) {
        const responseData = await response.json();
        console.log("✅ User created successfully:", responseData);
        await fetchUsers();
        setShowAddUser(false);
        setNewUser({
          name: "",
          email: "",
          phone: "",
          role: "Viewer",
        });
        if (responseData.invitation_email_sent) {
          setSuccessMessage(
            `Invitation sent to ${responseData.email}. They can set their password from the email link.`
          );
        } else {
          setSuccessMessage(
            `User ${responseData.email} was added, but the invitation email could not be sent. Ask them to use Forgot password on the sign-in page, or try adding them again.`
          );
        }
      } else {
        const errorData = await response.json();
        console.log("❌ Error response:", errorData);
        console.log("❌ Error detail:", errorData.detail);
        console.log(
          "❌ Full error object:",
          JSON.stringify(errorData, null, 2)
        );
        setError(errorData.detail || "Failed to add user");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await apiFetch(`/api/auth/users/${editingUser.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editUser),
      });

      if (response.ok) {
        await fetchUsers();
        setEditingUser(null);
        setEditUser({ name: "", phone: "", role: "Viewer", is_active: true });
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to update user");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const startEditUser = (user) => {
    setEditingUser(user);
    setEditUser({
      name: user.name,
      phone: user.phone || "",
      role: user.role,
      is_active: user.is_active,
    });
  };

  const handleRemoveUser = async (targetUser) => {
    const confirmMessage = `Remove ${targetUser.name} (${targetUser.email}) from your organization?\n\nThey will lose access immediately. This cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await apiFetch(`/api/auth/users/${targetUser.id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        const responseData = await response.json();
        await fetchUsers();
        setSuccessMessage(
          responseData.message ||
            `Removed ${targetUser.email} from your organization.`
        );
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to remove user");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditUser({ name: "", phone: "", role: "Viewer", is_active: true });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "Administrator":
        return "bg-red-900 text-red-300 border-red-700";
      case "Editor":
        return "bg-blue-900 text-blue-300 border-blue-700";
      case "Viewer":
        return "bg-gray-900 text-gray-300 border-gray-700";
      default:
        return "bg-gray-900 text-gray-300 border-gray-700";
    }
  };

  if (user.role !== "Administrator") {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-400">
            Only administrators can access user management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        hideHeader ? "bg-gray-900 p-4" : "min-h-screen bg-gray-900 p-6"
      }
    >
      <div className="max-w-7xl mx-auto">
        {!hideHeader ? (
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white mb-2">
              Users & roles
            </h1>
            <p className="text-gray-400">
              Invite people and set what they can view or edit.
            </p>
          </div>
        ) : null}

        {error && (
          <div className="mb-6 rounded-md bg-red-900/20 p-4 border border-red-700/50">
            <div className="text-sm text-red-300">{error}</div>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 rounded-md bg-emerald-900/20 p-4 border border-emerald-700/50">
            <div className="text-sm text-emerald-200">{successMessage}</div>
          </div>
        )}

        <div className="bg-gray-800 rounded-lg shadow-lg">
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">
                Organization Users
              </h2>
              {user.is_trial_user && !user.is_super_user ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-yellow-400">
                    This feature is restricted in trial mode
                  </span>
                  <button
                    disabled
                    className="px-4 py-2 bg-gray-600 text-gray-400 rounded-md cursor-not-allowed opacity-50"
                  >
                    Add User
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddUser(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Add User
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-400 mt-2">Loading users...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {users.map((rowUser) => (
                    <tr key={rowUser.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-white">
                            {rowUser.name}
                          </div>
                          <div className="text-sm text-gray-400">
                            {rowUser.email}
                          </div>
                          {rowUser.phone && (
                            <div className="text-sm text-gray-500">
                              {rowUser.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRoleColor(rowUser.role)}`}
                        >
                          {rowUser.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            rowUser.is_active
                              ? "bg-green-900 text-green-300"
                              : "bg-red-900 text-red-300"
                          }`}
                        >
                          {rowUser.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {rowUser.last_login
                          ? new Date(rowUser.last_login).toLocaleDateString()
                          : "Never"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => startEditUser(rowUser)}
                          className="text-blue-400 hover:text-blue-300 mr-4"
                        >
                          Edit
                        </button>
                        {rowUser.id !== user.id &&
                        !(user.is_trial_user && !user.is_super_user) ? (
                          <button
                            type="button"
                            onClick={() => void handleRemoveUser(rowUser)}
                            className="text-red-400 hover:text-red-300"
                          >
                            Remove
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add User Modal */}
        {showAddUser && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-700">
                <h3 className="text-lg font-semibold text-white">
                  Invite user
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  We&apos;ll email them a link to set their password.
                </p>
              </div>
              <form onSubmit={handleAddUser} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-600 rounded-md text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newUser.name}
                      onChange={(e) =>
                        setNewUser({ ...newUser, name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full px-3 py-2 border border-gray-600 rounded-md text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newUser.email}
                      onChange={(e) =>
                        setNewUser({ ...newUser, email: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      className="w-full px-3 py-2 border border-gray-600 rounded-md text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Phone number (optional)"
                      value={newUser.phone}
                      onChange={(e) =>
                        setNewUser({ ...newUser, phone: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Role *
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-600 rounded-md text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newUser.role}
                      onChange={(e) =>
                        setNewUser({ ...newUser, role: e.target.value })
                      }
                    >
                      {roles.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddUser(false)}
                    className="px-4 py-2 text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? "Sending invite…" : "Send invitation"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-700">
                <h3 className="text-lg font-semibold text-white">Edit User</h3>
              </div>
              <form onSubmit={handleEditUser} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-600 rounded-md text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editUser.name}
                      onChange={(e) =>
                        setEditUser({ ...editUser, name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      className="w-full px-3 py-2 border border-gray-600 rounded-md text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editUser.phone}
                      onChange={(e) =>
                        setEditUser({ ...editUser, phone: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Role *
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-600 rounded-md text-white bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editUser.role}
                      onChange={(e) =>
                        setEditUser({ ...editUser, role: e.target.value })
                      }
                    >
                      {roles.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                      checked={editUser.is_active}
                      onChange={(e) =>
                        setEditUser({
                          ...editUser,
                          is_active: e.target.checked,
                        })
                      }
                    />
                    <label
                      htmlFor="is_active"
                      className="ml-2 text-sm text-gray-300"
                    >
                      Active
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-4 py-2 text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? "Updating..." : "Update User"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
